import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* Data-driven: add a new entity here and it joins the ecosystem automatically */
const CENTER = { x: 50, y: 45 };

const ECOSYSTEM = [
  {
    id: "shipping",
    label: "Shipping & Logistics",
    pos: { x: 16, y: 20 },
    items: [
      "Ocean Freight — FCL / LCL",
      "Land Transportation",
      "Warehousing",
      "Global Logistics",
      "Port Services",
      "Container Services",
    ],
  },
  {
    id: "software",
    label: "Software Solutions",
    pos: { x: 84, y: 20 },
    items: [
      "Custom Software Development",
      "Process Automation & Integration",
      "Digital Transformation & Consulting",
    ],
  },
  {
    id: "fleet",
    label: "Fleet — M.V. Artax",
    pos: { x: 50, y: 88 },
    items: [
      "178 / 29 m — Dimensions",
      "22,800 t — Gross Tonnage",
      "37,000 t — Summer DWT",
    ],
  },
];

export default function Ecosystem() {
  const root = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "+=350%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      tl.fromTo(".eco-overline", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.3 }, 0);
      tl.fromTo(
        ".eco-center",
        { opacity: 0, scale: 0.7 },
        { opacity: 1, scale: 1, duration: 0.5, ease: "power2.out" },
        0.15
      );
      tl.fromTo(
        ".eco-spoke",
        { strokeDashoffset: 1 },
        { strokeDashoffset: 0, duration: 0.7, stagger: 0.15, ease: "power1.inOut" },
        0.6
      );
      tl.fromTo(
        ".eco-node",
        { opacity: 0, scale: 0.6 },
        { opacity: 1, scale: 1, duration: 0.45, stagger: 0.18, ease: "back.out(1.6)" },
        0.95
      );

      ECOSYSTEM.forEach((e, i) => {
        const at = 1.9 + i * 1.15;
        tl.fromTo(`.eco-ring-${i + 1}`, { opacity: 0 }, { opacity: 1, duration: 0.25 }, at);
        tl.fromTo(
          `.eco-detail-${i + 1}`,
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" },
          at + 0.05
        );
        if (i < ECOSYSTEM.length - 1) {
          tl.to(`.eco-detail-${i + 1}`, { opacity: 0, y: -40, duration: 0.35, ease: "power2.in" }, at + 1.0);
          tl.to(`.eco-ring-${i + 1}`, { opacity: 0, duration: 0.25 }, at + 1.0);
        }
      });

      const resolveAt = 1.9 + (ECOSYSTEM.length - 1) * 1.15 + 1.1;
      tl.to(".eco-ring", { opacity: 0.35, duration: 0.4 }, resolveAt);
      tl.to({}, { duration: 0.5 });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      id="ecosystem"
      className="relative h-[100svh] overflow-hidden"
      data-testid="ecosystem-scene"
      aria-label="The OA Holding ecosystem"
    >
      <p className="eco-overline absolute left-[6vw] top-[14vh] z-20 font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
        07 — OA Ecosystem
      </p>

      <div className="flex h-full flex-col items-center justify-center gap-6 px-[6vw] pt-[8vh] md:flex-row md:gap-12 md:pt-0">
        {/* radial diagram */}
        <div className="relative h-[42vh] w-full md:h-[68vh] md:w-[55%]" data-testid="ecosystem-diagram">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {ECOSYSTEM.map((e) => (
              <line
                key={e.id}
                className="eco-spoke"
                x1={CENTER.x}
                y1={CENTER.y}
                x2={e.pos.x}
                y2={e.pos.y}
                stroke="rgba(245,245,245,0.25)"
                strokeWidth="0.15"
                pathLength={1}
                strokeDasharray={1}
                strokeDashoffset={1}
              />
            ))}
          </svg>

          <div
            className="eco-center absolute flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center border border-white/25 bg-ink md:h-32 md:w-32"
            style={{ left: `${CENTER.x}%`, top: `${CENTER.y}%` }}
            data-testid="ecosystem-center"
          >
            <span className="font-display text-2xl font-black tracking-tight md:text-3xl">OA</span>
            <span className="mt-1 font-mono text-[8px] uppercase tracking-[0.25em] text-smoke">
              Holding
            </span>
          </div>

          {ECOSYSTEM.map((e, i) => (
            <div
              key={e.id}
              className="eco-node group absolute flex h-[76px] w-[76px] -translate-x-1/2 -translate-y-1/2 cursor-default items-center justify-center border border-white/20 bg-ink/90 backdrop-blur transition-colors duration-300 hover:border-amber/70 md:h-[104px] md:w-[104px]"
              style={{ left: `${e.pos.x}%`, top: `${e.pos.y}%` }}
              data-testid={`ecosystem-node-${e.id}`}
            >
              <span
                className={`eco-ring eco-ring-${i + 1} pointer-events-none absolute -inset-1.5 border border-amber`}
                aria-hidden="true"
              />
              <div className="flex flex-col items-center gap-1 px-1 text-center">
                <span className="font-mono text-[9px] text-amber">0{i + 1}</span>
                <span className="font-mono text-[8px] uppercase leading-tight tracking-[0.12em] text-bone md:text-[10px]">
                  {e.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* detail panel */}
        <div className="relative h-[34vh] w-full md:h-auto md:min-h-[50vh] md:w-[45%] md:border-l md:border-white/10 md:pl-12">
          {ECOSYSTEM.map((e, i) => (
            <div
              key={e.id}
              className={`eco-detail eco-detail-${i + 1} absolute inset-x-0 top-0 md:top-1/2 md:-translate-y-1/2`}
              data-testid={`ecosystem-detail-${e.id}`}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">
                Division 0{i + 1} / 0{ECOSYSTEM.length}
              </p>
              <h3 className="mt-4 font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter md:text-5xl">
                {e.label}
              </h3>
              <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
                {e.items.map((item, j) => (
                  <div key={item} className="flex items-baseline gap-4 py-2.5">
                    <span className="font-mono text-[10px] text-smoke">
                      0{i + 1}.{j + 1}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-bone/80 md:text-xs">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
